import { useRef, useState } from "react";
import { Crosshair } from "lucide-react";
import api from "../../lib/api";
import { useFocalMap, getObjectPosition } from "../../context/FocalMapContext";
import { toast } from "sonner";

/**
 * Click-to-set focal point on an image preview. Saved per image URL
 * (shared across every place that image is used on the site).
 */
export default function FocalPointPicker({ src, testIdPrefix = "focal" }) {
    const { map, refresh } = useFocalMap();
    const imgRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    if (!src) return null;
    const point = map[src];

    const onClick = async (e) => {
        const el = imgRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
        setSaving(true);
        try {
            await api.put("/admin/image-focal", { url: src, focal_x: x, focal_y: y });
            refresh();
            toast.success("Focal point saved");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save focal point");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-2" data-testid={`${testIdPrefix}-picker`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-ink-soft hover:text-blush transition-colors"
                data-testid={`${testIdPrefix}-toggle`}
            >
                <Crosshair size={12} /> {open ? "Close focal point" : "Set focal point"}
            </button>
            {open && (
                <div className="mt-2">
                    <p className="text-xs text-ink-soft mb-2">
                        Click the point that must stay visible when this image is cropped.
                    </p>
                    <div
                        ref={imgRef}
                        onClick={saving ? undefined : onClick}
                        className="relative w-full max-w-sm aspect-square overflow-hidden border border-[#EAE3DE] cursor-crosshair"
                    >
                        <img
                            src={src}
                            alt="Focal point preview"
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            style={{ objectPosition: getObjectPosition(map, src) }}
                        />
                        {point && (
                            <div
                                className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-white bg-blush/80 shadow pointer-events-none"
                                style={{ left: `${point[0]}%`, top: `${point[1]}%` }}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
